import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";

const transport = new DefaultChatTransport({ api: "/api/eve" });

const GREETING_TEXT =
  "Willkommen bei Energy Prime 360. Mein Name ist EVE. Ich bin Ihre digitale Assistentin und beantworte Fragen zu Neubau, Wohnersatzbau, Sanierungen, regenerativen Energien, Förderungen und Zuschüssen sowie steuerlichen Optimierungen. Wie kann ich Ihnen helfen?";

const GREETING: UIMessage = {
  id: "eve-greet-1",
  role: "assistant",
  parts: [{ type: "text", text: GREETING_TEXT }],
};

function messageText(m: UIMessage) {
  return m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
}

function pickVoice(): SpeechSynthesisVoice | null {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  const de = voices.filter((v) => v.lang?.toLowerCase().startsWith("de"));
  return (
    de.find((v) => /google/i.test(v.name) && /female|frau/i.test(v.name)) ||
    de.find((v) => /google/i.test(v.name)) ||
    de.find((v) => /anna|katja|hedda|petra|marlene|female|frau/i.test(v.name)) ||
    de[0] ||
    null
  );
}

function speak(text: string, onEnd?: () => void) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "de-DE";
    u.rate = 1.05;
    u.pitch = 1.0;
    const v = pickVoice();
    if (v) u.voice = v;
    if (onEnd) u.onend = onEnd;
    window.speechSynthesis.speak(u);
  } catch {}
}

export function EveAssistant() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceMode, setVoiceMode] = useState(true);
  const [greeted, setGreeted] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const recRef = useRef<any>(null);
  const lastSpokenRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const { messages, sendMessage, status, error } = useChat({
    transport,
    messages: [GREETING],
  });

  const busy = status === "submitted" || status === "streaming";

  // Listen to global "eve:open" event from nav button
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("eve:open", handler);
    return () => window.removeEventListener("eve:open", handler);
  }, []);

  // Init speech recognition (de-DE)
  useEffect(() => {
    const SR: any =
      (typeof window !== "undefined" &&
        ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) ||
      null;
    if (!SR) return;
    const r = new SR();
    r.lang = "de-DE";
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (e: any) => {
      const text = e.results?.[0]?.[0]?.transcript ?? "";
      if (text) void sendMessage({ text });
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recRef.current = r;
    return () => {
      try {
        r.stop();
      } catch {}
    };
  }, [sendMessage]);

  // Speak greeting once when opened in voice mode
  useEffect(() => {
    if (!open || !voiceMode || greeted) return;
    setGreeted(true);
    lastSpokenRef.current = GREETING.id;
    speak(GREETING_TEXT);
  }, [open, voiceMode, greeted]);

  // Speak new assistant messages when stream completes
  useEffect(() => {
    if (!open || !voiceMode) return;
    if (status !== "ready") return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    if (lastSpokenRef.current === last.id) return;
    const text = messageText(last);
    if (!text) return;
    lastSpokenRef.current = last.id;
    // Strip URLs / markdown for TTS
    const clean = text.replace(/https?:\/\/\S+/g, "").replace(/[#*_`>]/g, "");
    speak(clean);
  }, [messages, status, open, voiceMode]);

  // Auto-scroll
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, status]);

  // Focus textarea
  useEffect(() => {
    if (open && !voiceMode && !busy) inputRef.current?.focus();
  }, [open, voiceMode, busy]);

  const stopSpeaking = useCallback(() => {
    try {
      window.speechSynthesis?.cancel();
    } catch {}
  }, []);

  const toggleMic = () => {
    const r = recRef.current;
    if (!r) {
      alert("Dein Browser unterstützt keine Spracherkennung. Bitte nutze den Text-Modus.");
      setVoiceMode(false);
      return;
    }
    if (listening) {
      try { r.stop(); } catch {}
      setListening(false);
    } else {
      stopSpeaking();
      try {
        r.start();
        setListening(true);
      } catch {
        setListening(false);
      }
    }
  };

  const submitText = () => {
    const t = input.trim();
    if (!t || busy) return;
    setInput("");
    void sendMessage({ text: t });
  };

  const closePanel = () => {
    setOpen(false);
    stopSpeaking();
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-4 right-4 z-[110] w-[min(440px,calc(100vw-2rem))] h-[min(680px,calc(100vh-2rem))] rounded-2xl shadow-2xl bg-white border border-[var(--rule)] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between bg-navy text-white px-4 py-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[var(--leaf)] ring-2 ring-white/30 shrink-0">
                <svg viewBox="0 0 40 40" className="w-6 h-6" aria-hidden>
                  <rect x="8" y="10" width="24" height="22" rx="7" fill="none" stroke="white" strokeWidth="2.2" />
                  <line x1="20" y1="10" x2="20" y2="5" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
                  <circle cx="20" cy="4" r="1.6" fill="white" />
                  <circle cx="15" cy="19" r="2" fill="white" />
                  <circle cx="25" cy="19" r="2" fill="white" />
                  <path d="M15 25 Q20 29 25 25" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </span>
              <div className="leading-tight min-w-0">
                <div className="font-display text-sm font-semibold truncate">EVE</div>
                <div className="text-[11px] opacity-80 truncate">
                  Sprach-Assistentin · {voiceMode ? "Sprache aktiv" : "Text-Modus"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  if (voiceMode) stopSpeaking();
                  setVoiceMode((v) => !v);
                }}
                aria-label="Modus wechseln"
                className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20"
              >
                {voiceMode ? "Text" : "Sprache"}
              </button>
              <button
                onClick={closePanel}
                aria-label="Schließen"
                className="text-xl px-2 py-1 rounded hover:bg-white/10"
              >
                ×
              </button>
            </div>
          </div>

          {/* Transcript */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-[#f4f6fa] space-y-3">
            {messages.map((m) => {
              const text = messageText(m);
              if (!text) return null;
              const mine = m.role === "user";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                      mine
                        ? "bg-navy text-white rounded-br-md"
                        : "bg-white text-navy border border-[var(--rule)] rounded-bl-md"
                    }`}
                  >
                    {text}
                  </div>
                </div>
              );
            })}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-[var(--rule)] rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-navy/60 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-navy/40 animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-navy/40 animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-navy/40 animate-bounce" />
                </div>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-600 text-center">
                Es ist ein Fehler aufgetreten. Bitte erneut versuchen.
              </div>
            )}
          </div>

          {/* Quick goal actions */}
          <div className="px-3 pt-2 pb-1 flex gap-2 bg-white border-t border-[var(--rule)] shrink-0">
            <button
              onClick={() => { closePanel(); navigate({ to: "/projekt-beschreiben" }); }}
              className="flex-1 text-xs font-medium px-3 py-2 rounded-md bg-[var(--leaf)] text-white hover:bg-[var(--leaf-deep)] transition"
            >
              Projekt beschreiben
            </button>
            <button
              onClick={() => { closePanel(); navigate({ to: "/was-vergleichen-wir" }); }}
              className="flex-1 text-xs font-medium px-3 py-2 rounded-md bg-orange text-white hover:opacity-90 transition"
            >
              Angebote vergleichen
            </button>
          </div>

          {/* Composer */}
          <div className="bg-white px-3 py-3 shrink-0">
            {voiceMode ? (
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={toggleMic}
                  disabled={busy}
                  aria-label={listening ? "Aufnahme stoppen" : "Sprechen"}
                  className={`relative inline-flex items-center justify-center w-16 h-16 rounded-full text-white transition shadow-lg ${
                    listening ? "bg-red-500" : "bg-[var(--leaf)] hover:bg-[var(--leaf-deep)]"
                  } disabled:opacity-50`}
                >
                  {listening && (
                    <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />
                  )}
                  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor" aria-hidden>
                    <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 006 6.92V21h2v-3.08A7 7 0 0019 11h-2z" />
                  </svg>
                </button>
                <div className="text-xs text-navy/70 max-w-[180px]">
                  {listening ? "Ich höre zu …" : busy ? "EVE denkt nach …" : "Tippe das Mikro, um zu sprechen."}
                </div>
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitText();
                    }
                  }}
                  rows={1}
                  placeholder="Schreibe deine Frage an EVE …"
                  className="flex-1 min-h-[42px] max-h-32 resize-none rounded-lg border border-[var(--rule)] bg-white px-3 py-2.5 text-sm text-navy placeholder:text-navy/40 outline-none focus:border-[var(--leaf)] focus:ring-2 focus:ring-[var(--leaf)]/20"
                  disabled={busy}
                />
                <button
                  type="button"
                  onClick={submitText}
                  disabled={busy || !input.trim()}
                  aria-label="Senden"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[var(--leaf)] text-white shadow-sm hover:bg-[var(--leaf-deep)] disabled:opacity-50 disabled:cursor-not-allowed shrink-0 transition"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
                    <path d="M3.4 20.6L22 12 3.4 3.4 3 10l13 2-13 2 .4 6.6z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
